import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Eye, FileText, Paperclip, Plus, Save, Trash2, Upload, X } from 'lucide-react'
import toast from 'react-hot-toast'

import { showSaveSuccess } from '@/lib/swal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import type { SalesOrderLine } from '../types'
import { newSalesOrderLine } from '../types'
import { useAuthStore } from '@/store/auth.store'
import { salesOrderService, type SalesOrderBonusLineDto, type SalesOrderMasterResponse, type SalesOrderSaveLine } from '@/services/salesOrderService'
import { config } from '@/core/config'

const today = () => new Date().toISOString().slice(0, 10)

/** Resolve a server-relative attachment path to an absolute URL the browser can open. */
function resolveAttachmentUrl(pathOrUrl: string | null | undefined): string {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return encodeURI(raw)
  if (/^data:/i.test(raw)) return raw
  const normalizedPath = raw.replace(/\\/g, '/').replace(/^\/+/, '')
  const base = String(config.api.baseUrl || '').replace(/\/+$/, '')
  if (!base) return encodeURI(`/${normalizedPath}`)
  return encodeURI(`${base}/${normalizedPath}`)
}

type AttachmentFieldProps = {
  inputId: string
  mode: 'new' | 'edit'
  file: File | null
  existingUrl: string
  existingName: string
  onFileChange: (file: File | null) => void
}

/**
 * Single polished attachment control:
 *  - "new" mode: clean drop-zone style button to pick a file.
 *  - "edit" mode: shows the saved/picked file with a clickable filename,
 *    eye preview, Replace and Remove actions. The underlying file input
 *    is hidden so the chrome is consistent across browsers.
 */
const AttachmentField: React.FC<AttachmentFieldProps> = ({
  inputId,
  mode,
  file,
  existingUrl,
  existingName,
  onFileChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const hasExisting = Boolean(existingUrl)
  const hasAnyAttachment = hasExisting || Boolean(file)
  const openPicker = () => fileInputRef.current?.click()
  const clearFile = () => {
    if (fileInputRef.current) fileInputRef.current.value = ''
    onFileChange(null)
  }
  const fileName = file?.name || existingName || 'No file chosen'
  const showCard = mode === 'edit' && hasAnyAttachment

  return (
    <div className="space-y-1">
      <input
        ref={fileInputRef}
        id={inputId}
        type="file"
        className="sr-only"
        onChange={(e) => onFileChange(e.target.files?.[0] ?? null)}
        aria-label="File"
      />

      {showCard ? (
        <div className="flex h-10 w-full items-center gap-2 rounded-md border bg-background px-3 transition-colors hover:border-primary/40 focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20">
          <Paperclip className="h-4 w-4 shrink-0 text-primary" />
          {hasExisting && !file ? (
            <a
              href={existingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate text-sm text-foreground hover:text-primary hover:underline"
              title={fileName}
            >
              {fileName}
            </a>
          ) : (
            <span className="min-w-0 flex-1 truncate text-sm text-foreground">{fileName}</span>
          )}
          <div className="flex shrink-0 items-center gap-1">
            {hasExisting && !file ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2"
                onClick={() => window.open(existingUrl, '_blank', 'noopener,noreferrer')}
                title="View"
              >
                <Eye className="h-4 w-4" />
              </Button>
            ) : null}
            <Button type="button" variant="ghost" size="sm" className="h-8 gap-1 px-2" onClick={openPicker}>
              <Upload className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Replace</span>
            </Button>
            {file ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-muted-foreground hover:text-destructive"
                onClick={clearFile}
                title="Clear"
              >
                <X className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          className="flex h-10 w-full items-center gap-2 rounded-md border bg-background px-3 text-left transition-colors hover:border-primary/40 focus-within:border-primary/60 focus-within:ring-2 focus-within:ring-primary/20"
        >
          <Upload className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate text-sm">
            {file ? (
              <span className="text-foreground">{file.name}</span>
            ) : (
              <span className="text-muted-foreground">Choose a file to upload</span>
            )}
          </span>
          {file ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                clearFile()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  clearFile()
                }
              }}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-destructive"
              title="Clear"
            >
              <X className="h-4 w-4" />
            </span>
          ) : null}
        </button>
      )}

      {mode === 'edit' && file ? (
        <p className="truncate text-xs text-emerald-600 dark:text-emerald-400">
          New file selected — will replace on save
        </p>
      ) : null}
    </div>
  )
}

function mapApiLineToSalesOrderLine(
  x: any,
  taxRates: Array<{ id: number; percent: number }>,
): SalesOrderLine {
  const taxRaw = x?.tax ?? x?.Tax ?? x?.taxRateId ?? x?.TaxRateId
  let taxRateId =
    taxRaw !== undefined && taxRaw !== null && String(taxRaw) !== '' ? String(taxRaw) : ''
  const taxPctRaw = x?.taxPercent ?? x?.TaxPercent
  if (!taxRateId && taxPctRaw !== undefined && taxPctRaw !== null) {
    const pct = Number(taxPctRaw)
    const match = taxRates.find((t) => Math.abs(Number(t.percent) - pct) < 0.0001)
    if (match) taxRateId = String(match.id)
  }
  const ucp = x?.unitCostPrice ?? x?.UnitCostPrice
  return {
    id: Number(x?.id ?? x?.Id ?? 0),
    itemId: String(x?.itemId ?? x?.ItemId ?? ''),
    batchId: String(x?.batchId ?? x?.BatchId ?? ''),
    availableQuantity: 0,
    quantity: Number(x?.quantity ?? x?.Quantity ?? 0),
    unitPrice: Number(x?.unitPrice ?? x?.UnitPrice ?? 0),
    discountPercent: Number(x?.discountPercent ?? x?.DiscountPercent ?? 0),
    unitCostPrice: ucp === null || ucp === undefined ? 0 : Number(ucp),
    taxRateId,
  }
}

const lineTaxableAmount = (l: SalesOrderLine) => {
  const qty = Number(l.quantity || 0)
  const price = Number(l.unitPrice || 0)
  const discount = Number(l.discountPercent || 0)
  const gross = qty * price
  const net = gross * (1 - discount / 100)
  return Math.max(0, net)
}

/** Prefer the item's sales tax (`sellingTax`); otherwise the branch default sales rate. */
function resolveSalesTaxRateId(
  sellingTax: number | undefined | null,
  taxRates: Array<{ id: number }>,
  defaultId: string,
): string {
  const salesTaxId = sellingTax && Number(sellingTax) > 0 ? String(sellingTax) : ''
  if (salesTaxId && taxRates.some((t) => String(t.id) === salesTaxId)) return salesTaxId
  return defaultId
}

export const InventorySalesOrderEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const { id: routeOrderId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEdit = Boolean(routeOrderId && routeOrderId !== 'new')
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId)

  // Header fields
  const [orderDate, setOrderDate] = useState(today())
  const [expectedDate, setExpectedDate] = useState(today())
  const [reference, setReference] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [warehouseId, setWarehouseId] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('')
  const [paymentTerm, setPaymentTerm] = useState('')
  const [isDeliveryChallan, setIsDeliveryChallan] = useState(false)
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [lookupsReady, setLookupsReady] = useState(false)
  const [loadingOrder, setLoadingOrder] = useState(false)
  const [orderMaster, setOrderMaster] = useState<SalesOrderMasterResponse | null>(null)
  const [deletedRowIndices, setDeletedRowIndices] = useState<Set<number>>(() => new Set())

  // Lookups
  const [customers, setCustomers] = useState<Array<{ id: number; name: string }>>([])
  const [warehouses, setWarehouses] = useState<Array<{ id: number; name: string }>>([])
  const [paymentTerms, setPaymentTerms] = useState<Array<{ id: number; name: string }>>([])
  const [paymentMethods, setPaymentMethods] = useState<Array<{ id: number; name: string }>>([])
  const [items, setItems] = useState<
    Array<{ id: number; name: string; unitSellingPrice: number; unitCostPrice: number; sellingTax: number }>
  >([])
  const [taxRates, setTaxRates] = useState<Array<{ id: number; name: string; percent: number; isDefault?: boolean }>>([])
  const [batchesByItemId, setBatchesByItemId] = useState<Record<string, Array<{ id: number; name: string }>>>({})

  const lineTaxPercent = (l: SalesOrderLine) =>
    taxRates.find((t) => String(t.id) === l.taxRateId)?.percent ?? 0

  /** Default tax rate for newly added lines: prefer the 15% rate, then any
   *  rate whose name contains "VAT", else the first available one. */
  const defaultTaxRateId = useMemo(() => {
    if (taxRates.length === 0) return ''
    const markedDefault = taxRates.find((t) => t.isDefault)
    if (markedDefault) return String(markedDefault.id)
    const fifteen = taxRates.find((t) => Math.abs(Number(t.percent) - 15) < 0.0001)
    if (fifteen) return String(fifteen.id)
    const vat = taxRates.find((t) => /vat/i.test(String(t.name)))
    if (vat) return String(vat.id)
    return String(taxRates[0].id)
  }, [taxRates])
  const lineTaxAmount = (l: SalesOrderLine) => lineTaxableAmount(l) * (lineTaxPercent(l) / 100)
  const lineTotal = (l: SalesOrderLine) => lineTaxableAmount(l) + lineTaxAmount(l)

  const [lines, setLines] = useState<SalesOrderLine[]>(Array.from({ length: 3 }, () => newSalesOrderLine()))
  const lastQtyFetchKeyRef = useRef<Record<number, string>>({})
  const lastDiscountFetchKeyRef = useRef<Record<number, string>>({})
  const lastBonusFetchKeyRef = useRef<Record<number, string>>({})
  const [reservedBonus, setReservedBonus] = useState<SalesOrderBonusLineDto[]>([])
  const [liveBonusByIndex, setLiveBonusByIndex] = useState<
    Record<number, Array<{ itemId: number; itemName: string; batchId: number; quantity: number }>>
  >({})

  const bonusesForLine = (idx: number, line: SalesOrderLine) => {
    if (deletedRowIndices.has(idx)) return []
    if (orderMaster?.id && Number(line.id) > 0) {
      return reservedBonus
        .filter((b) => b.orderLineId === Number(line.id) && b.quantity > 0)
        .map((b) => ({
          itemId: b.itemId,
          itemName: b.itemName || items.find((i) => Number(i.id) === b.itemId)?.name || `Item ${b.itemId}`,
          batchId: b.batchId,
          quantity: b.quantity,
        }))
    }
    return liveBonusByIndex[idx] ?? []
  }

  useEffect(() => {
    if (!branchId) {
      setLookupsReady(false)
      return
    }
    setLookupsReady(false)
    const loadLookups = async () => {
      try {
        const [customersRes, warehousesRes, termsRes, methodsRes, itemsRes, taxRatesRes] = await Promise.all([
          salesOrderService.getCustomers(branchId),
          salesOrderService.getWarehouses(branchId),
          salesOrderService.getPaymentTerms(),
          salesOrderService.getPaymentMethods(),
          salesOrderService.getItems(branchId).catch(() => []),
          salesOrderService.getTaxRates(),
        ])
        setCustomers(customersRes)
        setWarehouses(warehousesRes)
        setPaymentTerms(termsRes)
        setPaymentMethods(methodsRes)
        setItems(itemsRes)
        setTaxRates(taxRatesRes)
        const isEditingRoute = Boolean(routeOrderId && routeOrderId !== 'new')
        if (!isEditingRoute) {
          setPaymentMethod((prev) => {
            if (prev && methodsRes.some((m) => String(m.id) === prev)) return prev
            return methodsRes[0] ? String(methodsRes[0].id) : ''
          })
          const firstWhId = String(warehousesRes?.[0]?.id ?? '')
          setWarehouseId((prev) => prev || firstWhId)
        }
        setLookupsReady(true)
      } catch (error: any) {
        setLookupsReady(false)
        toast.error(error?.response?.data?.message || 'Failed to load sales order lookups.')
      }
    }
    void loadLookups()
  }, [branchId, routeOrderId])

  useEffect(() => {
    if (!lookupsReady || !branchId || !routeOrderId || routeOrderId === 'new') return
    let cancelled = false
    const oid = Number(routeOrderId)
    if (!oid) return

    const run = async () => {
      try {
        setLoadingOrder(true)
        setOrderMaster(null)
        setReservedBonus([])
        setLiveBonusByIndex({})
        setDeletedRowIndices(new Set())
        lastQtyFetchKeyRef.current = {}
        lastDiscountFetchKeyRef.current = {}
        lastBonusFetchKeyRef.current = {}
        const [master, rawLines, bonusRows] = await Promise.all([
          salesOrderService.getSalesOrderById(oid),
          salesOrderService.getSalesOrderLinesByOrderId(oid),
          salesOrderService.getOrderBonusLines(oid),
        ])
        if (cancelled) return

        if (!master.id) {
          toast.error('Sales order not found.')
          navigate('/inventory/sales-orders')
          return
        }

        setOrderMaster(master)
        setReservedBonus(bonusRows)
        setLiveBonusByIndex({})
        setOrderDate(master.orderDate || today())
        setExpectedDate(master.estDeliveryDate || today())
        setReference(master.reference)
        setCustomerId(master.customerId ? String(master.customerId) : '')
        setWarehouseId(master.warehouseId ? String(master.warehouseId) : '')
        setPaymentMethod(master.paymentMethod ? String(master.paymentMethod) : '')
        setPaymentTerm(master.paymentTerm ? String(master.paymentTerm) : '')
        setIsDeliveryChallan(Boolean(master.isDeliveryChallan))

        const mapped =
          rawLines.length > 0
            ? rawLines.map((row) => mapApiLineToSalesOrderLine(row, taxRates))
            : [newSalesOrderLine()]
        setLines(mapped)

        const itemKeys = [...new Set(mapped.map((l) => l.itemId).filter(Boolean))]
        for (const itemKey of itemKeys) {
          if (cancelled) break
          const bId = Number(branchId)
          const itId = Number(itemKey)
          if (!bId || !itId) continue
          try {
            const batches = await salesOrderService.getBatches(bId, itId, Number(master.warehouseId || 0))
            if (!cancelled) {
              setBatchesByItemId((prev) => ({ ...prev, [itemKey]: batches }))
            }
          } catch {
            if (!cancelled) setBatchesByItemId((prev) => ({ ...prev, [itemKey]: [] }))
          }
        }
      } catch (error: any) {
        if (!cancelled) {
          toast.error(error?.response?.data?.message || 'Failed to load sales order.')
          navigate('/inventory/sales-orders')
        }
      } finally {
        if (!cancelled) setLoadingOrder(false)
      }
    }

    void run()
    return () => {
      cancelled = true
    }
    // taxRates is set in the same lookup batch as lookupsReady; omit from deps to avoid reloading the order when rates reference changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [lookupsReady, branchId, routeOrderId, navigate])

  useEffect(() => {
    if (routeOrderId !== 'new') return
    setOrderMaster(null)
    setDeletedRowIndices(new Set())
    lastQtyFetchKeyRef.current = {}
    lastDiscountFetchKeyRef.current = {}
    lastBonusFetchKeyRef.current = {}
    setReservedBonus([])
    setLiveBonusByIndex({})
    setIsDeliveryChallan(false)
    setLines(Array.from({ length: 3 }, () => newSalesOrderLine()))
  }, [routeOrderId])

  const addLines = (n: number) =>
    setLines((prev) => [
      ...prev,
      ...Array.from({ length: n }, () => ({ ...newSalesOrderLine(), taxRateId: defaultTaxRateId })),
    ])

  // Fill tax on lines that still have none / an invalid rate: item sales tax first, then default.
  useEffect(() => {
    if (taxRates.length === 0) return
    setLines((prev) => {
      let changed = false
      const next = prev.map((l) => {
        if (l.taxRateId && taxRates.some((t) => String(t.id) === l.taxRateId)) return l
        const it = items.find((i) => String(i.id) === l.itemId)
        const resolved = resolveSalesTaxRateId(it?.sellingTax, taxRates, defaultTaxRateId)
        if (!resolved || l.taxRateId === resolved) return l
        changed = true
        return { ...l, taxRateId: resolved }
      })
      return changed ? next : prev
    })
  }, [defaultTaxRateId, taxRates, items])

  const loadBatchesForItem = async (itemId: string) => {
    const bId = Number(branchId || 0)
    const itId = Number(itemId || 0)
    const whId = Number(warehouseId || 0)
    if (!bId || !itId) return
    if (batchesByItemId[itemId]) return
    try {
      const batches = await salesOrderService.getBatches(bId, itId, whId)
      setBatchesByItemId((prev) => ({ ...prev, [itemId]: batches }))
    } catch {
      setBatchesByItemId((prev) => ({ ...prev, [itemId]: [] }))
    }
  }

  const refreshAvailableQuantity = async (index: number, next?: { itemId?: string; batchId?: string }) => {
    const whId = Number(warehouseId || 0)
    if (!whId) return
    const row = lines[index]
    if (!row) return
    const itemId = Number((next?.itemId ?? row.itemId) || 0)
    const batchId = Number((next?.batchId ?? row.batchId) || 0)
    if (!itemId || !batchId) return
    try {
      const qty = await salesOrderService.getWarehouseItemQuantity(whId, batchId, itemId)
      updateLine(index, { availableQuantity: qty })
    } catch {
      updateLine(index, { availableQuantity: 0 })
    }
  }

  const refreshLineDiscountPercent = async (
    index: number,
    next?: { itemId?: string; batchId?: string },
  ) => {
    const bId = Number(branchId || 0)
    const itId = Number((next?.itemId ?? lines[index]?.itemId) || 0)
    const batchId = Number((next?.batchId ?? lines[index]?.batchId) || 0)
    const oDate = String(orderDate || '').trim()
    if (!bId || !itId || !oDate) return
    try {
      const pct = await salesOrderService.getDiscountPackagePercent({
        itemId: itId,
        branchId: bId,
        batchId,
        orderDate: oDate,
      })
      updateLine(index, { discountPercent: pct ?? 0 })
    } catch {
      // If discount API fails, keep the current scheme value.
    }
  }

  useEffect(() => {
    const whId = Number(warehouseId || 0)
    if (!whId) return

    lines.forEach((row, idx) => {
      if (deletedRowIndices.has(idx)) return
      const itemId = Number(row.itemId || 0)
      const batchId = Number(row.batchId || 0)
      if (!itemId || !batchId) return

      const key = `${whId}-${itemId}-${batchId}`
      if (lastQtyFetchKeyRef.current[idx] === key) return
      lastQtyFetchKeyRef.current[idx] = key
      void refreshAvailableQuantity(idx, { itemId: row.itemId, batchId: row.batchId })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, lines, deletedRowIndices])

  useEffect(() => {
    const bId = Number(branchId || 0)
    const oDate = String(orderDate || '').trim()
    if (!bId || !oDate) return

    lines.forEach((row, idx) => {
      if (deletedRowIndices.has(idx)) return
      const itemId = Number(row.itemId || 0)
      if (!itemId) return

      const batchId = Number(row.batchId || 0)
      const key = `${bId}-${oDate}-${itemId}-${batchId}`
      if (lastDiscountFetchKeyRef.current[idx] === key) return
      if (orderMaster?.id && Number(row.id) > 0 && lastDiscountFetchKeyRef.current[idx] === undefined) {
        lastDiscountFetchKeyRef.current[idx] = key
        return
      }
      lastDiscountFetchKeyRef.current[idx] = key
      void refreshLineDiscountPercent(idx, { itemId: row.itemId, batchId: row.batchId })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, orderDate, lines, deletedRowIndices, orderMaster?.id])

  useEffect(() => {
    const bId = Number(branchId || 0)
    const whId = Number(warehouseId || 0)
    const oDate = String(orderDate || '').trim()
    if (!bId || !whId || !oDate) return

    lines.forEach((row, idx) => {
      if (deletedRowIndices.has(idx)) return
      if (orderMaster?.id && Number(row.id) > 0) return

      const itemId = Number(row.itemId || 0)
      const qty = Number(row.quantity || 0)
      const batchId = Number(row.batchId || 0)
      if (!itemId || qty <= 0) {
        setLiveBonusByIndex((prev) => {
          if (!prev[idx]) return prev
          const next = { ...prev }
          delete next[idx]
          return next
        })
        return
      }

      const key = `${bId}-${whId}-${oDate}-${itemId}-${batchId}-${qty}`
      if (lastBonusFetchKeyRef.current[idx] === key) return
      lastBonusFetchKeyRef.current[idx] = key
      void salesOrderService
        .getBonusPackages({
          itemId,
          branchId: bId,
          warehouseId: whId,
          quantity: qty,
          orderDate: oDate,
          promotionBatchId: batchId,
        })
        .then((pkgs) => {
          setLiveBonusByIndex((prev) => ({
            ...prev,
            [idx]: pkgs.map((p) => ({
              itemId: p.bonusItemId,
              itemName:
                p.bonusItemName ||
                items.find((i) => Number(i.id) === p.bonusItemId)?.name ||
                `Item ${p.bonusItemId}`,
              batchId: p.bonusBatchId ?? 0,
              quantity: p.eligibleBonusQuantity,
            })),
          }))
        })
        .catch(() => {
          setLiveBonusByIndex((prev) => {
            const next = { ...prev }
            delete next[idx]
            return next
          })
        })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, warehouseId, orderDate, lines, deletedRowIndices, orderMaster?.id, items])

  useEffect(() => {
    if (!warehouseId) return
    setBatchesByItemId({})
    lines.forEach((row, idx) => {
      if (deletedRowIndices.has(idx)) return
      if (row.itemId) void loadBatchesForItem(row.itemId)
      if (row.itemId && row.batchId) void refreshAvailableQuantity(idx, { itemId: row.itemId, batchId: row.batchId })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, deletedRowIndices])

  const updateLine = (index: number, patch: Partial<SalesOrderLine>) => {
    setLines((prev) => {
      const next = [...prev]
      const row = next[index]
      if (!row) return prev
      const merged: SalesOrderLine = { ...row, ...patch }
      // Reset dependent selections when item changes
      if (patch.itemId !== undefined && patch.itemId !== row.itemId) {
        merged.batchId = ''
        merged.availableQuantity = 0
        merged.quantity = 0
        // Discount is item/date dependent; reset to allow auto-fill again.
        merged.discountPercent = 0
        delete lastDiscountFetchKeyRef.current[index]
      }
      if (patch.batchId !== undefined && patch.batchId !== row.batchId) {
        merged.availableQuantity = 0
        merged.quantity = 0
        merged.discountPercent = 0
        delete lastDiscountFetchKeyRef.current[index]
      }
      // Cap qty to available
      const avail = Number(merged.availableQuantity || 0)
      merged.quantity = Math.min(Number(merged.quantity || 0), avail || Number(merged.quantity || 0))
      next[index] = merged
      return next
    })
  }

  const handleLineDelete = (index: number) => {
    const row = lines[index]
    if (!row) return
    if (row.id > 0) {
      setDeletedRowIndices((prev) => {
        const next = new Set(prev)
        if (next.has(index)) next.delete(index)
        else next.add(index)
        return next
      })
      return
    }
    setLines((prev) => prev.filter((_, i) => i !== index))
    setDeletedRowIndices((prev) => {
      const next = new Set<number>()
      prev.forEach((j) => {
        if (j < index) next.add(j)
        else if (j > index) next.add(j - 1)
      })
      return next
    })
  }

  const totals = useMemo(() => {
    const taxable = lines.reduce(
      (sum, l, idx) => (deletedRowIndices.has(idx) ? sum : sum + lineTaxableAmount(l)),
      0,
    )
    const tax = lines.reduce(
      (sum, l, idx) => (deletedRowIndices.has(idx) ? sum : sum + lineTaxAmount(l)),
      0,
    )
    const total = lines.reduce(
      (sum, l, idx) => (deletedRowIndices.has(idx) ? sum : sum + lineTotal(l)),
      0,
    )
    return { taxable, tax, total }
  }, [lines, deletedRowIndices, taxRates])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!warehouseId) return toast.error('No warehouse found for selected branch.')
    if (!customerId) return toast.error('Customer is required.')
    if (!branchId) return toast.error('Branch is required.')
    if (!paymentMethod) return toast.error('Payment method is required.')

    const nowIso = new Date().toISOString()

    const orderIdForLines = isEdit && routeOrderId ? Number(routeOrderId) : 0

    const deletedStubs: SalesOrderSaveLine[] = []
    deletedRowIndices.forEach((idx) => {
      const l = lines[idx]
      if (!l || !l.id) return
      const taxRateIdNum = Number(l.taxRateId || 0)
      const taxPct = lineTaxPercent(l)
      deletedStubs.push({
        id: l.id,
        orderId: orderIdForLines,
        itemId: Number(l.itemId || 0),
        batchId: Number(l.batchId || 0),
        quantity: 0,
        unitPrice: 0,
        discountPercent: 0,
        discountAmount: 0,
        taxPercent: taxPct,
        taxAmount: 0,
        taxableAmount: 0,
        lineAmount: 0,
        unitCostPrice: Number(l.unitCostPrice || 0),
        tax: taxRateIdNum,
        isDeleted: 'true',
      })
    })

    const activeItemList = lines
      .map((l, idx) => ({ l, idx }))
      .filter(
        ({ l, idx }) =>
          !deletedRowIndices.has(idx) &&
          Number(l.itemId || 0) > 0 &&
          Number(l.batchId || 0) > 0 &&
          Number(l.quantity || 0) > 0,
      )
      .map(({ l }) => {
        const qty = Number(l.quantity || 0)
        const unitPrice = Number(l.unitPrice || 0)
        const discountPercent = Number(l.discountPercent || 0)
        const taxableAmount = lineTaxableAmount(l)
        const taxAmount = lineTaxAmount(l)
        const lineAmount = lineTotal(l)
        const discountAmount = Math.max(0, qty * unitPrice - taxableAmount)
        const taxRateId = Number(l.taxRateId || 0)
        const taxPercent = lineTaxPercent(l)
        return {
          id: Number(l.id || 0),
          orderId: orderIdForLines,
          itemId: Number(l.itemId || 0),
          batchId: Number(l.batchId || 0),
          quantity: qty,
          unitPrice,
          discountPercent,
          discountAmount,
          taxPercent,
          taxAmount,
          taxableAmount,
          lineAmount,
          unitCostPrice: Number(l.unitCostPrice || 0),
          tax: taxRateId,
          isDeleted: 'false',
        }
      })

    const itemList = [...activeItemList, ...deletedStubs]

    if (activeItemList.length === 0) return toast.error('Please add at least one item line.')
    if (activeItemList.some((row) => !row.tax || row.tax <= 0)) {
      return toast.error('Select a tax rate for every line item.')
    }

    const customerName =
      customers.find((c) => String(c.id) === String(customerId))?.name?.trim() ??
      orderMaster?.customerName?.trim() ??
      ''
    const warehouseName =
      warehouses.find((w) => String(w.id) === String(warehouseId))?.name?.trim() ??
      orderMaster?.warehouseName?.trim() ??
      ''

    const m = orderMaster
    const payload = {
      id: isEdit && routeOrderId ? Number(routeOrderId) : 0,
      quotCode: isEdit && m ? m.quotCode : '',
      code: isEdit && m ? m.code : '',
      customerName,
      warehouseName,
      reference: reference.trim(),
      orderDate,
      estDeliveryDate: expectedDate,
      customerId: Number(customerId || 0),
      paymentMethod: Number(paymentMethod !== '' ? paymentMethod : 0),
      taxableAmount: totals.taxable,
      taxAmount: totals.tax,
      discount: isEdit && m ? m.discount : 0,
      total: totals.total,
      salesPersonId: isEdit && m ? m.salesPersonId : 0,
      warehouseId: Number(warehouseId || 0),
      paymentTerm: Number(paymentTerm || 0),
      orderStatus: isEdit && m ? m.orderStatus : 0,
      isDeliveryChallan,
      attachmentPath: isEdit && m ? m.attachmentPath : '',
      createdDate: isEdit && m ? m.createdDate : nowIso,
      createdBy: isEdit && m ? m.createdBy : 0,
      modifiedDate: nowIso,
      modifiedBy: isEdit && m ? m.modifiedBy : 0,
      branchId: isEdit && m?.branchId ? m.branchId : Number(branchId || 0),
      attachmentFile,
      itemList,
    }

    const salesOrderLinesJson = JSON.stringify(itemList)
    console.log('[SalesOrders] Save/Update — master + lines', { ...payload, attachmentFile: payload.attachmentFile?.name ?? null })
    console.log('[SalesOrders] Save/Update — SalesOrderLinesJson', salesOrderLinesJson)

    try {
      setIsSaving(true)
      if (isEdit) await salesOrderService.update(payload)
      else await salesOrderService.save(payload)
      await showSaveSuccess(
        isEdit,
        isEdit ? 'Sales order updated successfully.' : 'Sales order saved successfully.',
      )
      navigate('/inventory/sales-orders')
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save sales order.')
    } finally {
      setIsSaving(false)
    }
  }

  const tableHeaderClass =
    'h-11 border-0 bg-primary/5 text-xs font-semibold uppercase tracking-wide text-primary hover:bg-primary/5 dark:bg-primary/10 dark:text-primary'

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Sales Orders</h1>
            <p className="text-muted-foreground text-sm">
              {isEdit && orderMaster?.code ? `Code: ${orderMaster.code}` : 'Sales Order Details'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="relative space-y-6">
        {loadingOrder ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/70 text-sm font-medium">
            Loading order…
          </div>
        ) : null}
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">Order Details</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Order Date</Label>
              <Input
                type="date"
                value={orderDate}
                onChange={(e) => {
                  setOrderDate(e.target.value)
                  // When the date changes, allow auto-fill to recompute for non-manual rows.
                  lastDiscountFetchKeyRef.current = {}
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Reference</Label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Customer</Label>
              <Select value={customerId || undefined} onValueChange={setCustomerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent searchable>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Warehouse dropdown removed; first branch warehouse is used automatically */}
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select
                value={paymentMethod !== '' ? paymentMethod : undefined}
                onValueChange={setPaymentMethod}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent searchable>
                  {paymentMethods.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Payment Term</Label>
              <Select value={paymentTerm || undefined} onValueChange={setPaymentTerm}>
                <SelectTrigger>
                  <SelectValue placeholder="Select payment term" />
                </SelectTrigger>
                <SelectContent searchable>
                  {paymentTerms.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Est Delivery Date</Label>
              <Input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="is-delivery-challan">Is Delivery Challan</Label>
              <Select
                value={isDeliveryChallan ? 'yes' : 'no'}
                onValueChange={(v) => setIsDeliveryChallan(v === 'yes')}
                disabled={loadingOrder}
              >
                <SelectTrigger id="is-delivery-challan">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="sales-order-attachment">File</Label>
              {(() => {
                const existingPath = orderMaster?.attachmentPath || ''
                const existingUrl = resolveAttachmentUrl(existingPath)
                const existingName = existingPath
                  ? existingPath.split(/[\\/]/).pop() || 'attachment'
                  : ''
                return (
                  <AttachmentField
                    inputId="sales-order-attachment"
                    mode={isEdit ? 'edit' : 'new'}
                    file={attachmentFile}
                    existingUrl={isEdit ? existingUrl : ''}
                    existingName={isEdit ? existingName : ''}
                    onFileChange={setAttachmentFile}
                  />
                )
              })()}
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="flex flex-col gap-4 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">Ordered Items</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(1)}>
                <Plus className="mr-1 h-4 w-4" />
                Add Row
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(3)}>
                Add 3 Rows
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(5)}>
                Add 5 Rows
              </Button>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    {[
                      'Item',
                      'Batch',
                      'Available quantity',
                      'Quantity',
                      'Unit price',
                      'Discount %',
                      'Taxable amount',
                      'Tax %',
                      'Tax amount',
                      'Total',
                      '',
                    ].map((h) => (
                      <TableHead key={h} className={tableHeaderClass}>
                        {h}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l, idx) => {
                    const isRowDeleted = deletedRowIndices.has(idx)
                    const lineBonuses = bonusesForLine(idx, l)
                    return (
                    <React.Fragment key={l.id ? `line-${l.id}` : `new-${idx}`}>
                    <TableRow
                      className={
                        isRowDeleted ? 'bg-red-50 hover:bg-red-50/90 dark:bg-red-900/20 dark:hover:bg-red-900/25' : ''
                      }
                    >
                      <TableCell className="py-2">
                        <Select
                          value={l.itemId || undefined}
                          onValueChange={(v) => {
                            const it = items.find((i) => String(i.id) === v)
                            updateLine(idx, {
                              itemId: v,
                              unitPrice: Number(it?.unitSellingPrice || 0),
                              unitCostPrice: Number(it?.unitCostPrice || 0),
                              taxRateId: resolveSalesTaxRateId(it?.sellingTax, taxRates, defaultTaxRateId),
                            })
                            void loadBatchesForItem(v)
                          }}
                          disabled={isRowDeleted || loadingOrder}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select item" />
                          </SelectTrigger>
                          <SelectContent searchable>
                            {items.map((it) => (
                              <SelectItem key={it.id} value={String(it.id)}>
                                {it.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="py-2">
                        <Select
                          value={l.batchId || undefined}
                          onValueChange={(v) => {
                            console.log('[SalesOrders] Batch selected:', { row: idx, itemId: l.itemId, batchId: v, warehouseId })
                            updateLine(idx, { batchId: v })
                            void refreshAvailableQuantity(idx, { itemId: l.itemId, batchId: v })
                          }}
                          disabled={!l.itemId || isRowDeleted || loadingOrder}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select batch" />
                          </SelectTrigger>
                          <SelectContent searchable>
                            {(batchesByItemId[l.itemId] || []).map((b) => (
                              <SelectItem key={b.id} value={String(b.id)}>
                                {b.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="py-2">
                        <Input readOnly className="h-9 text-center bg-muted/50" value={l.availableQuantity || 0} />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input
                          type="number"
                          min={0}
                          className="h-9 text-center"
                          value={l.quantity ?? ''}
                          onChange={(e) => updateLine(idx, { quantity: Number(e.target.value) || 0 })}
                          disabled={isRowDeleted || loadingOrder}
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input
                          type="number"
                          min={0}
                          readOnly
                          className="h-9 text-center bg-muted/50"
                          value={l.unitPrice ?? ''}
                          disabled={isRowDeleted || loadingOrder}
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input
                          type="number"
                          min={0}
                          readOnly
                          className="h-9 text-center bg-muted/50"
                          value={l.discountPercent ?? ''}
                          title="Discount comes from the active scheme"
                          disabled={isRowDeleted || loadingOrder}
                        />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input readOnly className="h-9 text-center bg-muted/50" value={lineTaxableAmount(l).toFixed(2)} />
                      </TableCell>
                      <TableCell className="py-2 min-w-[180px]">
                        <Select
                          value={l.taxRateId || undefined}
                          onValueChange={(v) => updateLine(idx, { taxRateId: v })}
                          disabled={isRowDeleted || loadingOrder}
                        >
                          <SelectTrigger className="h-9 min-w-[180px]">
                            <SelectValue placeholder="Tax rate" />
                          </SelectTrigger>
                          <SelectContent searchable>
                            {taxRates.map((t) => (
                              <SelectItem key={t.id} value={String(t.id)}>
                                {t.name} ({t.percent}%)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="py-2">
                        <Input readOnly className="h-9 text-center bg-muted/50" value={lineTaxAmount(l).toFixed(2)} />
                      </TableCell>
                      <TableCell className="py-2">
                        <Input readOnly className="h-9 text-center bg-muted/50" value={lineTotal(l).toFixed(2)} />
                      </TableCell>
                      <TableCell className="py-2 text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={loadingOrder}
                          className={
                            isRowDeleted
                              ? 'text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950/30'
                              : 'text-destructive hover:bg-destructive/10'
                          }
                          title={isRowDeleted ? t('lookups.undoDelete') : t('lookups.deleteRow')}
                          onClick={() => handleLineDelete(idx)}
                        >
                          {isRowDeleted ? (
                            <span className="text-xs font-semibold">{t('lookups.undo')}</span>
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {lineBonuses.map((b, bi) => (
                      <TableRow key={`bonus-${idx}-${b.itemId}-${b.batchId}-${bi}`} className="bg-muted/40">
                        <TableCell className="py-1 text-sm text-muted-foreground" colSpan={2}>
                          Bonus: {b.itemName}
                          {b.batchId > 0 ? ` · Batch ${b.batchId}` : ''}
                        </TableCell>
                        <TableCell className="py-1" />
                        <TableCell className="py-1 text-center text-sm">{b.quantity}</TableCell>
                        <TableCell className="py-1 text-sm text-muted-foreground" colSpan={7}>
                          Free of charge
                        </TableCell>
                      </TableRow>
                    ))}
                    </React.Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="mt-6 border-t bg-primary/5 p-4 sm:px-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <Label className="text-sm text-muted-foreground">{t('invoices.taxableAmount')}</Label>
                  <p className="text-lg font-semibold">{totals.taxable.toFixed(2)}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">{t('invoices.totalVat')}</Label>
                  <p className="text-lg font-semibold">{totals.tax.toFixed(2)}</p>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">{t('invoices.total')}</Label>
                  <p className="text-lg font-semibold">{totals.total.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate('/inventory/sales-orders')}>
            Back
          </Button>
          <Button type="submit" disabled={isSaving || loadingOrder}>
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : isEdit ? 'Update' : 'Save'}
          </Button>
        </div>
      </form>
    </div>
  )
}

